/**
 * Company + contact selection, as one unit.
 *
 * They're paired because they're dependent: contacts are only meaningful within a company, and
 * the contact list must re-scope the moment the company changes. Splitting them into two
 * independent selects is how a ticket ends up attributed to a person at a different client.
 */

import { useState } from 'react';

import { useCompanies, useContacts } from '@/hooks/useServiceDesk';
import { Select } from '@/components/common/Select';

export function CompanyPicker({
  companyId,
  contactId,
  onCompanyChange,
  onContactChange,
}: {
  companyId: string | null;
  contactId: string | null;
  onCompanyChange: (id: string | null) => void;
  onContactChange: (id: string | null) => void;
}) {
  const [search, setSearch] = useState('');
  const { data: companies } = useCompanies({ page_size: 50, search: search || undefined });
  // Skipped entirely until a company is chosen — there is no useful "all contacts everywhere".
  const { data: contacts } = useContacts({
    page_size: 100,
    company_id: companyId ?? undefined,
  });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-200">
          Company
        </span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter companies…"
          className="input-base mb-1"
          aria-label="Filter companies"
        />
        <Select
          ariaLabel="Company"
          value={companyId ?? ''}
          onChange={(next) => onCompanyChange(next || null)}
          options={[
            { value: '', label: 'Unassigned' },
            ...(companies?.items ?? []).map((company) => ({
              value: company.id,
              label: company.name,
            })),
          ]}
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-200">
          Contact
        </span>
        <Select
          ariaLabel="Contact"
          value={contactId ?? ''}
          onChange={(next) => onContactChange(next || null)}
          disabled={!companyId}
          options={[
            { value: '', label: companyId ? 'None' : 'Pick a company first' },
            ...(contacts?.items ?? []).map((contact) => ({
              value: contact.id,
              label: contact.full_name,
              hint: contact.title ?? undefined,
            })),
          ]}
        />
      </label>
    </div>
  );
}
